import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check, Lock } from "lucide-react";
import { useAccess } from "../../../../../../utils/hooks/access";
import { useCompanyCurrency, useCompanyTimeFormat } from "../../../../../../utils/hooks/company";
import { renderDurationAsFormat } from "../../../../../../utils/helpers/date";
import { currencyFormatter } from "../../../../../../utils/helpers/currencyFormatter";
import { getTimeSheetDuration } from "../../../../../TimeSheet/components/TimeSheetTable/useColumns/useDurationColumn";
import { getTimeSheetTotal } from "../../../../helpers/getTimesheetTotal";
import { cn } from "../../../../../../lib/utils";

interface CollapsedSummaryProps {
    data: any[];
    type: string;
}

function CollapsedSummary({ data, type }: CollapsedSummaryProps) {
    const { t } = useTranslation();
    const isMyTimesheet = type === "my-timesheet";
    const canViewRates = useAccess(isMyTimesheet ? "canViewMyRates" : "canViewRates");
    const timeFormat = useCompanyTimeFormat();
    const currency = useCompanyCurrency();

    const realRows = useMemo(() => data.filter((r: any) => !r.isAdded), [data]);

    const paidHours = useMemo(() =>
        realRows
            .filter((r: any) => r?.hourType !== "unpaid" && !r.payCode?.excludeFromPaidHours && !r.isNonProductiveTime)
            .reduce((total: number, r: any) => total + getTimeSheetDuration(r), 0),
        [realRows]
    );

    const paidWages = useMemo(() =>
        realRows
            .filter((r: any) => !r.isNonProductiveTime)
            .reduce((total: number, r: any) => total + (getTimeSheetTotal(r) ?? 0), 0),
        [realRows]
    );

    const isApproved = useMemo(() => realRows.every((r: any) => r.status === "approved"), [realRows]);
    const isPartiallyApproved = useMemo(() => !isApproved && realRows.some((r: any) => r.status === "approved"), [realRows, isApproved]);
    const isLocked = useMemo(() => realRows.some((r: any) => r.locked), [realRows]);

    if (realRows.length === 0) return null;

    return (
        <div className="tw-w-full tw-flex tw-items-center tw-gap-4 tw-px-4 tw-py-2 tw-bg-muted/50 tw-border-t tw-border-primary/10 tw-text-sm tw-text-left tw-rounded-b-lg">
            <span className="tw-text-xs tw-font-medium tw-text-muted-foreground tw-tabular-nums">
                {realRows.length} {t(realRows.length === 1 ? "entry" : "entries")}
            </span>

            <div className="tw-flex tw-items-baseline tw-gap-1">
                <span className="tw-text-xs tw-font-semibold tw-text-foreground tw-tabular-nums">
                    {renderDurationAsFormat(paidHours, timeFormat)}
                </span>
                <span className="tw-text-xs tw-text-muted-foreground">{t("hrs")}</span>
            </div>

            {canViewRates && (
                <span className="tw-text-xs tw-font-semibold tw-text-primary tw-tabular-nums">
                    {currencyFormatter(paidWages || 0, currency)}
                </span>
            )}

            <div className="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
                {(isApproved || isPartiallyApproved) && (
                    <span
                        className={cn(
                            "tw-inline-flex tw-items-center tw-gap-1 tw-rounded-sm tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium",
                            isApproved
                                ? "tw-bg-emerald-100 tw-text-emerald-700"
                                : "tw-bg-yellow-100 tw-text-yellow-700"
                        )}
                    >
                        <Check size={10} />
                        {isApproved ? t("approved") : t("partial")}
                    </span>
                )}

                {isLocked && (
                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-sm tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-amber-100 tw-text-amber-700">
                        <Lock size={10} />
                        {t("locked")}
                    </span>
                )}
            </div>
        </div>
    );
}

export default CollapsedSummary;
